import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { 
  Activity, AlertOctagon, Download, User, Clock, Trash2, 
  CheckCircle2, XCircle, ClipboardCheck, Calendar, ChevronDown,
  AlertTriangle, Fuel, Package, ChevronRight, ChevronLeft
} from "lucide-react";
import { ReportsSection as PartsInventoryReports } from "@/components/inventory/ReportsSection";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { API } from "@/utils/constants";
import { getOutOfServiceDuration } from "@/utils/helpers";

export const ReportsSection = ({ vehicles, serviceIntervals, users = [], currentUser }) => {
  const [activeReport, setActiveReport] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [reportLogs, setReportLogs] = useState([]);
  
  // DVIR Audit state
  const [selectedDriver, setSelectedDriver] = useState("");
  const [auditData, setAuditData] = useState(null);
  const [loadingAudit, setLoadingAudit] = useState(false);

  // User Activity Report state
  const [activityPeriodType, setActivityPeriodType] = useState("month");
  const [activityDate, setActivityDate] = useState(new Date().toISOString().split('T')[0]);
  const [activityMonth, setActivityMonth] = useState(new Date().getMonth() + 1);
  const [activityQuarter, setActivityQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [activityYear, setActivityYear] = useState(new Date().getFullYear());
  const [activityData, setActivityData] = useState(null);
  const [loadingActivity, setLoadingActivity] = useState(false);

  // DVIR Audit Report state - enhanced with date filters
  const [dvirPeriodType, setDvirPeriodType] = useState("month");
  const [dvirDate, setDvirDate] = useState(new Date().toISOString().split('T')[0]);
  const [dvirMonth, setDvirMonth] = useState(new Date().getMonth() + 1);
  const [dvirQuarter, setDvirQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [dvirYear, setDvirYear] = useState(new Date().getFullYear());

  // Fuel Report state
  const [fuelPeriodType, setFuelPeriodType] = useState("month");
  const [fuelDate, setFuelDate] = useState(new Date().toISOString().split('T')[0]);
  const [fuelMonth, setFuelMonth] = useState(new Date().getMonth() + 1);
  const [fuelQuarter, setFuelQuarter] = useState(Math.ceil((new Date().getMonth() + 1) / 3));
  const [fuelYear, setFuelYear] = useState(new Date().getFullYear());
  const [fuelData, setFuelData] = useState(null);
  const [loadingFuel, setLoadingFuel] = useState(false);

  useEffect(() => {
    const fetchReportLogs = async () => {
      try {
        const response = await axios.get(`${API}/report-logs`);
        setReportLogs(response.data);
      } catch (error) {
        console.error("Failed to fetch report logs", error);
      }
    };
    fetchReportLogs();
  }, []);

  const logReportGeneration = async (reportType) => {
    try {
      const userName = currentUser ? `${currentUser.first_name} ${currentUser.last_name}` : "Fleet Admin";
      await axios.post(`${API}/report-logs`, {
        report_type: reportType,
        generated_by: userName
      });
      const response = await axios.get(`${API}/report-logs`);
      setReportLogs(response.data);
    } catch (error) {
      console.error("Failed to log report generation", error);
    }
  };

  const handleDeleteReportLog = async (logId) => {
    if (!window.confirm("Are you sure you want to delete this log entry?")) return;
    try {
      await axios.delete(`${API}/report-logs/${logId}`);
      toast.success("Log entry deleted");
      const response = await axios.get(`${API}/report-logs`);
      setReportLogs(response.data);
    } catch (error) {
      toast.error("Failed to delete log entry");
    }
  };
  
  const today = new Date().toLocaleDateString('en-US', { 
    weekday: 'long', 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const reports = [
    {
      id: "health",
      title: "Vehicle Health Report",
      description: "Overview of all vehicles' service status including B.I.T., D.O.T., and Oil Change due dates",
      icon: Activity,
      color: "#007AFF",
      bg: "rgba(0, 122, 255, 0.1)"
    },
    {
      id: "availability",
      title: "Vehicle Availability Report",
      description: "Shows which trucks are out of service and should not be used",
      icon: AlertOctagon,
      color: "#FF3B30",
      bg: "rgba(255, 59, 48, 0.1)"
    },
    {
      id: "dvir_audit",
      title: "DVIR Audit Report",
      description: "Audit driver inspection compliance - select a driver and time period",
      icon: ClipboardCheck,
      color: "#34C759",
      bg: "rgba(52, 199, 89, 0.1)"
    }
  ];

  // Get drivers from users (filter by position if needed)
  const drivers = users.filter(u => u.is_active);

  const generateAuditReport = async () => {
    if (!selectedDriver) {
      toast.error("Please select a driver");
      return;
    }
    
    setLoadingAudit(true);
    try {
      const params = { 
        inspector_id: selectedDriver,
        period: dvirPeriodType 
      };
      if (dvirPeriodType === "date") {
        params.date = dvirDate;
      } else if (dvirPeriodType === "month") {
        params.year = dvirYear;
        params.month = dvirMonth;
      } else if (dvirPeriodType === "quarter") {
        params.year = dvirYear;
        params.quarter = dvirQuarter;
      } else if (dvirPeriodType === "year") {
        params.year = dvirYear;
      }
      
      const response = await axios.get(`${API}/dvir/audit`, { params });
      setAuditData(response.data);
      setActiveReport("dvir_audit");
    } catch (error) {
      console.error("Failed to generate audit report", error);
      toast.error("Failed to generate audit report");
    } finally {
      setLoadingAudit(false);
    }
  };

  const generateActivityReport = async () => {
    setLoadingActivity(true);
    try {
      const params = { period: activityPeriodType };
      if (activityPeriodType === "date") {
        params.date = activityDate;
      } else if (activityPeriodType === "month") {
        params.year = activityYear;
        params.month = activityMonth;
      } else if (activityPeriodType === "quarter") {
        params.year = activityYear;
        params.quarter = activityQuarter;
      } else if (activityPeriodType === "year") {
        params.year = activityYear;
      }
      
      const response = await axios.get(`${API}/reports/user-activity`, { params });
      setActivityData(response.data);
      setActiveReport("user_activity");
    } catch (error) {
      console.error("Failed to generate activity report", error);
      toast.error("Failed to generate activity report");
    } finally {
      setLoadingActivity(false);
    }
  };

  const generateFuelReport = async () => {
    setLoadingFuel(true);
    try {
      const params = { period: fuelPeriodType };
      if (fuelPeriodType === "date") {
        params.date = fuelDate;
      } else if (fuelPeriodType === "month") {
        params.year = fuelYear;
        params.month = fuelMonth;
      } else if (fuelPeriodType === "quarter") {
        params.year = fuelYear;
        params.quarter = fuelQuarter;
      } else if (fuelPeriodType === "year") {
        params.year = fuelYear;
      }
      
      const response = await axios.get(`${API}/reports/fuel`, { params });
      setFuelData(response.data);
      setActiveReport("fuel_report");
    } catch (error) {
      console.error("Failed to generate fuel report", error);
      toast.error("Failed to generate fuel report");
    } finally {
      setLoadingFuel(false);
    }
  };

  const getServiceStatus = (vehicle, serviceType) => {
    const lastService = vehicle[`last_${serviceType}`];
    const lastMileage = vehicle[`last_${serviceType.replace('_inspection', '').replace('_change', '')}_mileage`] || 
                        vehicle[`last_${serviceType === 'oil_change' ? 'oil' : serviceType.split('_')[0]}_mileage`];
    
    if (!lastService) return { status: "overdue", label: "Never", mileage: null, color: "#FF3B30" };
    
    const interval = serviceIntervals.find(i => i.service_type === `${serviceType.replace('_', '_')}`);
    const formattedDate = new Date(lastService).toLocaleDateString();
    
    if (!interval) return { status: "ok", label: formattedDate, mileage: lastMileage, color: "#34C759" };
    
    // On-demand services have no schedule - always show as OK
    if (interval.interval_unit === "on_demand") {
      return { status: "ok", label: formattedDate, mileage: lastMileage, color: "#34C759" };
    }
    
    const lastDate = new Date(lastService);
    const now = new Date();
    let dueDate;
    
    if (interval.interval_unit === "days") {
      dueDate = new Date(lastDate.getTime() + interval.interval_value * 24 * 60 * 60 * 1000);
    } else if (interval.interval_unit === "months") {
      dueDate = new Date(lastDate);
      dueDate.setMonth(dueDate.getMonth() + interval.interval_value);
    } else if (interval.interval_unit === "yearly") {
      dueDate = new Date(lastDate);
      dueDate.setFullYear(dueDate.getFullYear() + interval.interval_value);
    } else {
      return { status: "ok", label: formattedDate, mileage: lastMileage, color: "#34C759" };
    }
    
    if (now > dueDate) {
      return { status: "overdue", label: formattedDate, mileage: lastMileage, color: "#FF3B30" };
    }
    return { status: "ok", label: formattedDate, mileage: lastMileage, color: "#34C759" };
  };

  const generatePDF = async (reportType) => {
    setGenerating(true);
    setActiveReport(reportType);
    
    await new Promise(resolve => setTimeout(resolve, 500));
    
    try {
      const html2pdf = (await import('html2pdf.js')).default;
      const element = document.getElementById('report-content');
      
      if (!element) {
        toast.error("Failed to generate PDF");
        setGenerating(false);
        return;
      }

      const filename = reportType === 'health' 
        ? `Vehicle_Health_Report_${new Date().toISOString().split('T')[0]}.pdf`
        : `Vehicle_Availability_Report_${new Date().toISOString().split('T')[0]}.pdf`;

      const opt = {
        margin: [10, 10, 10, 10],
        filename: filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, logging: false },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' }
      };

      await html2pdf().set(opt).from(element).save();
      
      await logReportGeneration(reportType);
      
      toast.success(`${reportType === 'health' ? 'Vehicle Health' : 'Vehicle Availability'} Report downloaded`);
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error("Failed to generate PDF");
    }
    
    setGenerating(false);
  };

  const unavailableVehicles = vehicles.filter(v => v.is_out_of_service);

  return (
    <div className="p-4 sm:p-6 lg:p-8 pt-16 sm:pt-6 lg:pt-8" style={{ touchAction: 'pan-y' }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Reports</h1>
          <p className="text-sm sm:text-base text-slate-500 mt-1">Generate and download fleet reports as PDF</p>
        </div>
        {activeReport && activeReport !== "dvir_audit" && activeReport !== "parts" && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={() => generatePDF(activeReport)}
              disabled={generating}
              data-testid="btn-download-pdf"
            >
              <Download className="w-4 h-4 mr-2" /> 
              {generating ? "Generating..." : "Download PDF"}
            </Button>
          </div>
        )}
      </div>

      {!activeReport ? (
        <>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {reports.filter(r => r.id !== 'dvir_audit').map((report) => (
            <button
              key={report.id}
              onClick={() => generatePDF(report.id)}
              disabled={generating}
              className="bg-white rounded-xl border border-slate-200 p-6 text-left hover:shadow-lg hover:border-blue-300 transition-all group disabled:opacity-50"
              data-testid={`report-${report.id}`}
            >
              <div className="flex items-start gap-4">
                <div 
                  className="w-12 h-12 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: report.bg }}
                >
                  <report.icon className="w-6 h-6" style={{ color: report.color }} />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-lg text-slate-900 mb-1">{report.title}</h3>
                  <p className="text-sm text-slate-500">{report.description}</p>
                </div>
                <Download className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-all" />
              </div>
            </button>
          ))}
        </div>

        {/* Parts & Inventory Reports */}
        <button
          onClick={() => setActiveReport("parts")}
          className="mt-6 w-full bg-white rounded-xl border border-slate-200 p-4 sm:p-6 text-left hover:shadow-lg hover:border-blue-300 transition-all group flex items-start gap-4"
          data-testid="report-parts"
        >
          <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform" style={{ backgroundColor: "rgba(79, 70, 229, 0.1)" }}>
            <Package className="w-6 h-6 text-indigo-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-lg text-slate-900 mb-1">Parts &amp; Inventory Reports</h3>
            <p className="text-sm text-slate-500">Inventory value, purchasing, unit costs, tires, salvage, cores and requests — with CSV export.</p>
          </div>
          <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-all flex-shrink-0" />
        </button>
        
        {/* DVIR Audit Report - Special Card with Date Selectors */}
        <div className="mt-6 bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
          <div className="flex items-start gap-4 mb-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "rgba(52, 199, 89, 0.1)" }}
            >
              <ClipboardCheck className="w-6 h-6 text-green-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-slate-900 mb-1">DVIR Audit Report</h3>
              <p className="text-sm text-slate-500">Audit driver inspection compliance - select a driver and time period</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Driver Selector */}
            <div>
              <Label className="text-sm font-medium text-slate-700">Select Driver</Label>
              <Select value={selectedDriver} onValueChange={setSelectedDriver}>
                <SelectTrigger className="mt-1.5 h-11" data-testid="select-audit-driver">
                  <SelectValue placeholder="Choose a driver..." />
                </SelectTrigger>
                <SelectContent>
                  {drivers.map((driver) => (
                    <SelectItem key={driver.id} value={driver.id}>
                      {driver.first_name} {driver.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Period Type Selector */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { value: "date", label: "Date" },
                { value: "month", label: "Month" },
                { value: "quarter", label: "Quarter" },
                { value: "year", label: "Year" }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setDvirPeriodType(option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    dvirPeriodType === option.value
                      ? "bg-green-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`dvir-period-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            
            {/* Dynamic Filter Controls */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              {dvirPeriodType === "date" && (
                <div className="sm:col-span-2">
                  <Label className="text-sm font-medium text-slate-700">Select Date</Label>
                  <Input
                    type="date"
                    value={dvirDate}
                    onChange={(e) => setDvirDate(e.target.value)}
                    className="mt-1.5 h-11"
                    data-testid="dvir-date-input"
                  />
                </div>
              )}
              
              {dvirPeriodType === "month" && (
                <>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Month</Label>
                    <Select value={String(dvirMonth)} onValueChange={(v) => setDvirMonth(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="dvir-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["January", "February", "March", "April", "May", "June", 
                          "July", "August", "September", "October", "November", "December"
                        ].map((month, idx) => (
                          <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Year</Label>
                    <Select value={String(dvirYear)} onValueChange={(v) => setDvirYear(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="dvir-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map((year) => (
                          <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {dvirPeriodType === "quarter" && (
                <>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Quarter</Label>
                    <Select value={String(dvirQuarter)} onValueChange={(v) => setDvirQuarter(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="dvir-quarter-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Q1 (Jan - Mar)</SelectItem>
                        <SelectItem value="2">Q2 (Apr - Jun)</SelectItem>
                        <SelectItem value="3">Q3 (Jul - Sep)</SelectItem>
                        <SelectItem value="4">Q4 (Oct - Dec)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Year</Label>
                    <Select value={String(dvirYear)} onValueChange={(v) => setDvirYear(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="dvir-quarter-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map((year) => (
                          <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {dvirPeriodType === "year" && (
                <div className="sm:col-span-2">
                  <Label className="text-sm font-medium text-slate-700">Year</Label>
                  <Select value={String(dvirYear)} onValueChange={(v) => setDvirYear(parseInt(v))}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="dvir-full-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map((year) => (
                        <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <Button 
                onClick={generateAuditReport}
                disabled={loadingAudit || !selectedDriver}
                className="w-full sm:w-auto h-11 bg-green-600 hover:bg-green-700"
                data-testid="btn-generate-audit"
              >
                {loadingAudit ? "Loading..." : "Generate Report"}
              </Button>
            </div>
          </div>
        </div>

        {/* User Activity Report - Special Card with Date Selectors */}
        <div className="mt-6 bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
          <div className="flex items-start gap-4 mb-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "rgba(139, 92, 246, 0.1)" }}
            >
              <User className="w-6 h-6 text-purple-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-slate-900 mb-1">User Activity Report</h3>
              <p className="text-sm text-slate-500">Track all entries made by users - service records, DVIRs, and service requests</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Period Type Selector */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { value: "date", label: "Date" },
                { value: "month", label: "Month" },
                { value: "quarter", label: "Quarter" },
                { value: "year", label: "Year" }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setActivityPeriodType(option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    activityPeriodType === option.value
                      ? "bg-purple-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`activity-period-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            
            {/* Dynamic Filter Controls */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              {activityPeriodType === "date" && (
                <div className="sm:col-span-2">
                  <Label className="text-sm font-medium text-slate-700">Select Date</Label>
                  <Input
                    type="date"
                    value={activityDate}
                    onChange={(e) => setActivityDate(e.target.value)}
                    className="mt-1.5 h-11"
                    data-testid="activity-date-input"
                  />
                </div>
              )}
              
              {activityPeriodType === "month" && (
                <>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Month</Label>
                    <Select value={String(activityMonth)} onValueChange={(v) => setActivityMonth(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="activity-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["January", "February", "March", "April", "May", "June", 
                          "July", "August", "September", "October", "November", "December"
                        ].map((month, idx) => (
                          <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Year</Label>
                    <Select value={String(activityYear)} onValueChange={(v) => setActivityYear(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="activity-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map((year) => (
                          <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {activityPeriodType === "quarter" && (
                <>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Quarter</Label>
                    <Select value={String(activityQuarter)} onValueChange={(v) => setActivityQuarter(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="activity-quarter-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Q1 (Jan - Mar)</SelectItem>
                        <SelectItem value="2">Q2 (Apr - Jun)</SelectItem>
                        <SelectItem value="3">Q3 (Jul - Sep)</SelectItem>
                        <SelectItem value="4">Q4 (Oct - Dec)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Year</Label>
                    <Select value={String(activityYear)} onValueChange={(v) => setActivityYear(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="activity-quarter-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map((year) => (
                          <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {activityPeriodType === "year" && (
                <div className="sm:col-span-2">
                  <Label className="text-sm font-medium text-slate-700">Year</Label>
                  <Select value={String(activityYear)} onValueChange={(v) => setActivityYear(parseInt(v))}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="activity-full-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map((year) => (
                        <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <Button 
                onClick={generateActivityReport}
                disabled={loadingActivity}
                className="w-full sm:w-auto h-11 bg-purple-600 hover:bg-purple-700"
                data-testid="btn-generate-activity"
              >
                {loadingActivity ? "Loading..." : "Generate Report"}
              </Button>
            </div>
          </div>
        </div>

        {/* Fuel Report - Special Card with Date Selectors */}
        <div className="mt-6 bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
          <div className="flex items-start gap-4 mb-4">
            <div 
              className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: "rgba(16, 185, 129, 0.1)" }}
            >
              <Fuel className="w-6 h-6 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold text-lg text-slate-900 mb-1">Fuel Report</h3>
              <p className="text-sm text-slate-500">View all fuel entries by date, month, quarter, or year</p>
            </div>
          </div>
          
          <div className="space-y-4">
            {/* Period Type Selector */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {[
                { value: "date", label: "Date" },
                { value: "month", label: "Month" },
                { value: "quarter", label: "Quarter" },
                { value: "year", label: "Year" }
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setFuelPeriodType(option.value)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                    fuelPeriodType === option.value
                      ? "bg-emerald-600 text-white"
                      : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                  }`}
                  data-testid={`fuel-period-${option.value}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            
            {/* Dynamic Filter Controls */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
              {fuelPeriodType === "date" && (
                <div className="sm:col-span-2">
                  <Label className="text-sm font-medium text-slate-700">Select Date</Label>
                  <Input
                    type="date"
                    value={fuelDate}
                    onChange={(e) => setFuelDate(e.target.value)}
                    className="mt-1.5 h-11"
                    data-testid="fuel-date-input"
                  />
                </div>
              )}
              
              {fuelPeriodType === "month" && (
                <>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Month</Label>
                    <Select value={String(fuelMonth)} onValueChange={(v) => setFuelMonth(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="fuel-month-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {["January", "February", "March", "April", "May", "June", 
                          "July", "August", "September", "October", "November", "December"
                        ].map((month, idx) => (
                          <SelectItem key={idx} value={String(idx + 1)}>{month}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Year</Label>
                    <Select value={String(fuelYear)} onValueChange={(v) => setFuelYear(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="fuel-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map((year) => (
                          <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {fuelPeriodType === "quarter" && (
                <>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Quarter</Label>
                    <Select value={String(fuelQuarter)} onValueChange={(v) => setFuelQuarter(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="fuel-quarter-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">Q1 (Jan - Mar)</SelectItem>
                        <SelectItem value="2">Q2 (Apr - Jun)</SelectItem>
                        <SelectItem value="3">Q3 (Jul - Sep)</SelectItem>
                        <SelectItem value="4">Q4 (Oct - Dec)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-700">Year</Label>
                    <Select value={String(fuelYear)} onValueChange={(v) => setFuelYear(parseInt(v))}>
                      <SelectTrigger className="mt-1.5 h-11" data-testid="fuel-quarter-year-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2025, 2026, 2027].map((year) => (
                          <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
              
              {fuelPeriodType === "year" && (
                <div className="sm:col-span-2">
                  <Label className="text-sm font-medium text-slate-700">Year</Label>
                  <Select value={String(fuelYear)} onValueChange={(v) => setFuelYear(parseInt(v))}>
                    <SelectTrigger className="mt-1.5 h-11" data-testid="fuel-full-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2024, 2025, 2026, 2027].map((year) => (
                        <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <Button 
                onClick={generateFuelReport}
                disabled={loadingFuel}
                className="w-full sm:w-auto h-11 bg-emerald-600 hover:bg-emerald-700"
                data-testid="btn-generate-fuel-report"
              >
                {loadingFuel ? "Loading..." : "Generate Report"}
              </Button>
            </div>
          </div>
        </div>
        
        {reportLogs.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-semibold text-slate-900 mb-4">Report Generation Log</h2>
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Report Type</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Generated By</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Date & Time</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Days Until Archive</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 w-16"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {reportLogs.map((log) => {
                    const createdDate = new Date(log.created_at);
                    const archiveDate = new Date(createdDate.getTime() + 30 * 24 * 60 * 60 * 1000);
                    const daysUntilArchive = Math.ceil((archiveDate - new Date()) / (1000 * 60 * 60 * 24));
                    return (
                      <tr key={log.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {log.report_type === 'health' ? (
                              <Activity className="w-4 h-4 text-blue-600" />
                            ) : (
                              <AlertOctagon className="w-4 h-4 text-red-500" />
                            )}
                            <span className="font-medium text-slate-900">
                              {log.report_type === 'health' ? 'Vehicle Health Report' : 'Vehicle Availability Report'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4" />
                            {log.generated_by}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600">
                          {createdDate.toLocaleString()}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            daysUntilArchive <= 7 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            <Clock className="w-3 h-3" />
                            {daysUntilArchive} days
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleDeleteReportLog(log.id)}
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                            data-testid={`btn-delete-log-${log.id}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-400 mt-2">* Logs are automatically archived after 30 days</p>
          </div>
        )}
        
        {/* Bottom padding for smooth scrolling */}
        <div className="h-8 sm:h-4"></div>
      </> 
      ) : activeReport === "parts" ? (
        <div>
          <button
            onClick={() => setActiveReport(null)}
            className="text-sm text-blue-600 hover:text-blue-700 font-medium mb-4 inline-flex items-center gap-1"
            data-testid="parts-reports-back"
          >
            <ChevronLeft className="w-4 h-4" /> Back to Reports
          </button>
          <PartsInventoryReports />
        </div>
      ) : activeReport === "health" ? (
        <div className="report-container print:p-0">
          <div className="flex items-center justify-between mb-6 print:mb-4">
            <Button 
              variant="ghost" 
              onClick={() => setActiveReport(null)}
              className="print:hidden"
            >
              ← Back to Reports
            </Button>
          </div>

          <div id="report-content" className="bg-white rounded-xl border border-slate-200 overflow-hidden print:border-0 print:shadow-none">
            <div className="p-6 border-b border-slate-200 print:p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_vehicle-tracker-162/artifacts/n3an3dwp_djlj7u57_PEW.jpeg" 
                    alt="Pacific Expressway" 
                    className="h-12 w-auto object-contain"
                  />
                  <div>
                    <h1 className="text-lg font-bold text-slate-900">Pacific Expressway</h1>
                    <p className="text-xs text-slate-500">Fleet Maintenance</p>
                  </div>
                </div>
                <div className="text-right">
                  <Activity className="w-6 h-6 text-blue-600 print:hidden ml-auto mb-1" />
                </div>
              </div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold text-slate-900">Vehicle Health Report</h2>
              </div>
              <p className="text-sm text-slate-500">Generated: {today}</p>
              <p className="text-sm text-slate-500">Total Vehicles: {vehicles.length}</p>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Vehicle</th>
                    <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Type</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">B.I.T.</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">D.O.T.</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Oil Change</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Mileage</th>
                    <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {vehicles.map((vehicle) => {
                    const bitStatus = getServiceStatus(vehicle, "bit_inspection");
                    const dotStatus = getServiceStatus(vehicle, "dot_inspection");
                    const oilStatus = getServiceStatus(vehicle, "oil_change");
                    
                    return (
                      <tr key={vehicle.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900">{vehicle.unit_number}</div>
                          <div className="text-xs text-slate-500">{vehicle.year} {vehicle.make} {vehicle.model}</div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`type-badge ${vehicle.vehicle_type}`}>
                            {vehicle.vehicle_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center">
                            <div 
                              className="w-3 h-3 rounded-full mb-1"
                              style={{ backgroundColor: bitStatus.color }}
                            />
                            <span className="text-xs text-slate-600">{bitStatus.label}</span>
                            {bitStatus.mileage > 0 && (
                              <span className="text-xs text-slate-400">@ {bitStatus.mileage.toLocaleString()} mi</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center">
                            <div 
                              className="w-3 h-3 rounded-full mb-1"
                              style={{ backgroundColor: dotStatus.color }}
                            />
                            <span className="text-xs text-slate-600">{dotStatus.label}</span>
                            {dotStatus.mileage > 0 && (
                              <span className="text-xs text-slate-400">@ {dotStatus.mileage.toLocaleString()} mi</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center">
                            <div 
                              className="w-3 h-3 rounded-full mb-1"
                              style={{ backgroundColor: oilStatus.color }}
                            />
                            <span className="text-xs text-slate-600">{oilStatus.label}</span>
                            {oilStatus.mileage > 0 && (
                              <span className="text-xs text-slate-400">@ {oilStatus.mileage.toLocaleString()} mi</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-sm text-slate-700">
                          {(vehicle.mileage || 0).toLocaleString()} mi
                        </td>
                        <td className="px-4 py-3 text-center">
                          {vehicle.is_out_of_service ? (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                              <XCircle className="w-3 h-3" /> OOS
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                              <CheckCircle2 className="w-3 h-3" /> Active
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="p-6 bg-slate-50 border-t border-slate-200 print:p-4">
              <h3 className="font-semibold text-slate-900 mb-3">Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <div className="text-2xl font-bold text-slate-900">{vehicles.length}</div>
                  <div className="text-xs text-slate-500">Total Vehicles</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <div className="text-2xl font-bold text-red-600">
                    {vehicles.filter(v => !v.last_bit_inspection).length}
                  </div>
                  <div className="text-xs text-slate-500">Need B.I.T.</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <div className="text-2xl font-bold text-red-600">
                    {vehicles.filter(v => !v.last_dot_inspection).length}
                  </div>
                  <div className="text-xs text-slate-500">Need D.O.T.</div>
                </div>
                <div className="bg-white rounded-lg p-3 border border-slate-200">
                  <div className="text-2xl font-bold text-red-600">
                    {vehicles.filter(v => !v.last_oil_change).length}
                  </div>
                  <div className="text-xs text-slate-500">Need Oil Change</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : activeReport === "availability" ? (
        <div className="report-container print:p-0">
          <div className="flex items-center justify-between mb-6 print:mb-4">
            <Button 
              variant="ghost" 
              onClick={() => setActiveReport(null)}
              className="print:hidden"
            >
              ← Back to Reports
            </Button>
          </div>

          <div id="report-content" className="bg-white rounded-xl border border-slate-200 overflow-hidden print:border-0 print:shadow-none">
            <div className="p-6 border-b border-slate-200 bg-red-50 print:p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_vehicle-tracker-162/artifacts/n3an3dwp_djlj7u57_PEW.jpeg" 
                    alt="Pacific Expressway" 
                    className="h-12 w-auto object-contain"
                  />
                  <div>
                    <h1 className="text-lg font-bold text-slate-900">Pacific Expressway</h1>
                    <p className="text-xs text-slate-500">Fleet Maintenance</p>
                  </div>
                </div>
                <AlertOctagon className="w-8 h-8 text-red-600" />
              </div>
              <div className="flex items-center gap-3 mb-2">
                <h2 className="text-2xl font-bold text-slate-900">DO NOT USE - Out of Service Vehicles</h2>
              </div>
              <p className="text-sm text-slate-500">Generated: {today}</p>
              <div className="mt-3 p-3 bg-red-100 rounded-lg border border-red-200">
                <p className="text-sm font-semibold text-red-800">
                  {unavailableVehicles.length} vehicle{unavailableVehicles.length !== 1 ? 's' : ''} currently out of service
                </p>
              </div>
            </div>

            {unavailableVehicles.length === 0 ? (
              <div className="p-12 text-center">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-slate-900 mb-2">All Vehicles Available</h3>
                <p className="text-slate-500">No vehicles are currently out of service.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-red-50">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-red-800">Unit #</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-red-800">Vehicle</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-red-800">Type</th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wider text-red-800">Reason</th>
                      <th className="text-center px-4 py-3 text-xs font-semibold uppercase tracking-wider text-red-800">Duration</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-red-100">
                    {unavailableVehicles.map((vehicle) => (
                      <tr key={vehicle.id} className="bg-red-50/50 hover:bg-red-50">
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                              <XCircle className="w-5 h-5 text-red-600" />
                            </div>
                            <span className="font-bold text-lg text-red-700">{vehicle.unit_number}</span>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="font-medium text-slate-900">{vehicle.year} {vehicle.make} {vehicle.model}</div>
                          <div className="text-xs text-slate-500">VIN: ...{vehicle.vin.slice(-8)}</div>
                        </td>
                        <td className="px-4 py-4">
                          <span className={`type-badge ${vehicle.vehicle_type}`}>
                            {vehicle.vehicle_type.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm font-medium text-red-700">
                            {vehicle.out_of_service_reason || "No reason provided"}
                          </div>
                        </td>
                        <td className="px-4 py-4 text-center">
                          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold bg-red-100 text-red-700">
                            <Clock className="w-4 h-4" />
                            {vehicle.out_of_service_date ? getOutOfServiceDuration(vehicle.out_of_service_date) : "N/A"}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="p-4 bg-slate-50 border-t border-slate-200 text-center text-xs text-slate-500">
              This report lists all vehicles that should NOT be dispatched or used. Please verify status before assigning any vehicle.
            </div>
          </div>
        </div>
      ) : activeReport === "dvir_audit" && auditData ? (
        <div>
          {/* Report Content */}
          <div id="report-content" className="bg-white rounded-xl border border-slate-200 overflow-hidden print:border-0 print:shadow-none">
            {/* Report Header */}
            <div className="p-6 border-b border-slate-200 bg-green-50 print:p-4">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_vehicle-tracker-162/artifacts/n3an3dwp_djlj7u57_PEW.jpeg" 
                    alt="Pacific Expressway" 
                    className="h-12 w-auto object-contain"
                  />
                  <div>
                    <h1 className="text-lg font-bold text-slate-900">Pacific Expressway</h1>
                    <p className="text-xs text-slate-500">Fleet Maintenance</p>
                  </div>
                </div>
                <ClipboardCheck className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">DVIR Audit Report</h2>
              <p className="text-sm text-slate-600">
                Driver: <span className="font-semibold">{drivers.find(d => d.id === selectedDriver)?.first_name} {drivers.find(d => d.id === selectedDriver)?.last_name}</span>
              </p>
              <p className="text-sm text-slate-500">
                Period: {auditData.period_label || "Selected Period"} • 
                {new Date(auditData.start_date).toLocaleDateString()} - {new Date(auditData.end_date).toLocaleDateString()}
              </p>
            </div>

            {/* Summary Stats */}
            <div className="p-6 border-b">
              <h3 className="font-semibold text-slate-900 mb-4">Compliance Summary</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-slate-900">{auditData.summary.total_dvirs}</div>
                  <div className="text-xs text-slate-500 mt-1">Total DVIRs</div>
                </div>
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-slate-900">{auditData.summary.days_with_dvirs}</div>
                  <div className="text-xs text-slate-500 mt-1">Days with DVIRs</div>
                </div>
                <div className={`rounded-lg p-4 text-center ${auditData.summary.compliance_rate >= 80 ? 'bg-green-50' : auditData.summary.compliance_rate >= 50 ? 'bg-amber-50' : 'bg-red-50'}`}>
                  <div className={`text-3xl font-bold ${auditData.summary.compliance_rate >= 80 ? 'text-green-600' : auditData.summary.compliance_rate >= 50 ? 'text-amber-600' : 'text-red-600'}`}>
                    {auditData.summary.compliance_rate}%
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Compliance Rate</div>
                </div>
                <div className={`rounded-lg p-4 text-center ${auditData.summary.dvirs_with_defects > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <div className={`text-3xl font-bold ${auditData.summary.dvirs_with_defects > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    {auditData.summary.dvirs_with_defects}
                  </div>
                  <div className="text-xs text-slate-500 mt-1">DVIRs with Defects</div>
                </div>
              </div>
            </div>

            {/* DVIRs by Date */}
            <div className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Inspection History</h3>
              {auditData.dvirs_by_date.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">No DVIRs found for this period</p>
                  <p className="text-sm text-slate-500 mt-1">This driver has not completed any inspections during the selected time period.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {auditData.dvirs_by_date.map((dateGroup) => (
                    <div key={dateGroup.date} className="border border-slate-200 rounded-lg overflow-hidden">
                      <div className="bg-slate-50 px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-slate-500" />
                          <span className="font-medium text-slate-900">
                            {new Date(dateGroup.date + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                          </span>
                        </div>
                        <span className="text-sm text-slate-500">{dateGroup.count} inspection{dateGroup.count !== 1 ? 's' : ''}</span>
                      </div>
                      <div className="divide-y divide-slate-100">
                        {dateGroup.dvirs.map((dvir) => (
                          <div key={dvir.id} className="px-4 py-3 flex items-center justify-between hover:bg-slate-50">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${dvir.has_defects ? 'bg-red-100' : 'bg-green-100'}`}>
                                {dvir.has_defects ? (
                                  <AlertTriangle className="w-4 h-4 text-red-600" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                                )}
                              </div>
                              <div>
                                <div className="font-medium text-slate-900">Unit {dvir.unit_number}</div>
                                <div className="text-xs text-slate-500">
                                  {new Date(dvir.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  {dvir.mileage && ` • ${dvir.mileage.toLocaleString()} mi`}
                                </div>
                              </div>
                            </div>
                            {dvir.has_defects && (
                              <span className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded-full">
                                {dvir.defect_count} defect{dvir.defect_count !== 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 text-center text-xs text-slate-500">
              DVIR Audit Report • Generated {new Date().toLocaleString()} • Pacific Expressway Fleet Management
            </div>
          </div>
          
          {/* Action Buttons */}
          <div className="mt-4 flex flex-col gap-2 sm:gap-3 print:hidden">
            <Button 
              onClick={() => generatePDF("dvir_audit")}
              disabled={generating}
              className="w-full h-11 sm:h-12 bg-green-600 hover:bg-green-700 flex items-center justify-center gap-2 text-sm sm:text-base"
              data-testid="btn-download-audit-pdf"
            >
              <Download className="w-4 h-4" />
              {generating ? "Generating PDF..." : "Download PDF"}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => { setActiveReport(null); setAuditData(null); }} 
              className="w-full h-11 sm:h-12 text-sm sm:text-base"
            >
              Close
            </Button>
          </div>
        </div>
      ) : activeReport === "user_activity" && activityData ? (
        /* User Activity Report Display */
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <Button 
              variant="ghost" 
              onClick={() => { setActiveReport(null); setActivityData(null); }}
              className="self-start"
            >
              ← Back to Reports
            </Button>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-purple-100">
                    <User className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h1 className="text-2xl font-bold text-slate-900">User Activity Report</h1>
                    <p className="text-sm text-slate-500">
                      {activityData.period_label || "Selected Period"}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-3xl font-bold text-purple-600">{activityData.total_entries}</div>
                  <div className="text-sm text-slate-500">Total Entries</div>
                </div>
              </div>
            </div>

            {/* User Summary Cards */}
            <div className="p-6 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">Entries by User</h2>
              {activityData.user_summary.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No entries found for this period</p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {activityData.user_summary.map((user, idx) => (
                    <div 
                      key={user.name} 
                      className={`rounded-lg p-4 border ${idx === 0 ? 'bg-purple-50 border-purple-200' : 'bg-slate-50 border-slate-200'}`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${idx === 0 ? 'bg-purple-600 text-white' : 'bg-slate-300 text-slate-700'}`}>
                          <User className="w-5 h-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-semibold text-slate-900 truncate">{user.name}</div>
                          <div className={`text-2xl font-bold ${idx === 0 ? 'text-purple-600' : 'text-slate-700'}`}>
                            {user.total_entries} <span className="text-sm font-normal text-slate-500">entries</span>
                          </div>
                        </div>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center text-xs">
                        <div className="bg-white rounded p-2">
                          <div className="font-bold text-blue-600">{user.service_records}</div>
                          <div className="text-slate-500">Services</div>
                        </div>
                        <div className="bg-white rounded p-2">
                          <div className="font-bold text-green-600">{user.dvirs}</div>
                          <div className="text-slate-500">DVIRs</div>
                        </div>
                        <div className="bg-white rounded p-2">
                          <div className="font-bold text-amber-600">{user.service_requests}</div>
                          <div className="text-slate-500">Requests</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Entry List */}
            <div className="p-6">
              <h2 className="text-lg font-semibold text-slate-900 mb-4">All Entries</h2>
              {activityData.entries.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No entries found for this period</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {activityData.entries.map((entry) => (
                    <div key={entry.id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        entry.type === 'service_record' ? 'bg-blue-100' :
                        entry.type === 'dvir' ? 'bg-green-100' : 'bg-amber-100'
                      }`}>
                        {entry.type === 'service_record' ? (
                          <Activity className="w-4 h-4 text-blue-600" />
                        ) : entry.type === 'dvir' ? (
                          <ClipboardCheck className="w-4 h-4 text-green-600" />
                        ) : (
                          <AlertTriangle className="w-4 h-4 text-amber-600" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-slate-900">
                            {entry.type === 'service_record' ? 'Service: ' :
                             entry.type === 'dvir' ? 'DVIR: ' : 'Request: '}
                            {entry.subtype}
                          </span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">
                            {vehicles.find(v => v.id === entry.vehicle_id)?.unit_number || 'Unknown'}
                          </span>
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                          <User className="w-3 h-3 inline mr-1" />
                          {entry.performed_by}
                          <span className="mx-2">•</span>
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(entry.created_at).toLocaleString()}
                        </div>
                        {entry.notes && (
                          <div className="text-xs text-slate-500 mt-1 truncate">{entry.notes}</div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              variant="outline" 
              onClick={() => { setActiveReport(null); setActivityData(null); }} 
              className="w-full h-11 sm:h-12 text-sm sm:text-base"
            >
              Close
            </Button>
          </div>
        </div>
      ) : activeReport === "fuel_report" && fuelData ? (
        /* Fuel Report Display */
        <div className="space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 mb-6">
            <Button 
              variant="ghost" 
              onClick={() => { setActiveReport(null); setFuelData(null); }}
              className="self-start"
            >
              ← Back to Reports
            </Button>
          </div>

          <div id="report-content" className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            {/* Report Header */}
            <div className="p-6 border-b border-slate-200 bg-emerald-50">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-4">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_vehicle-tracker-162/artifacts/n3an3dwp_djlj7u57_PEW.jpeg" 
                    alt="Pacific Expressway" 
                    className="h-12 w-auto object-contain"
                  />
                  <div>
                    <h1 className="text-lg font-bold text-slate-900">Pacific Expressway</h1>
                    <p className="text-xs text-slate-500">Fleet Maintenance</p>
                  </div>
                </div>
                <Fuel className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Fuel Report</h2>
              <p className="text-sm text-slate-600">
                Period: <span className="font-semibold">{fuelData.period_label}</span>
              </p>
              <p className="text-sm text-slate-500">
                {new Date(fuelData.start_date).toLocaleDateString()} - {new Date(fuelData.end_date).toLocaleDateString()}
              </p>
            </div>

            {/* Summary Stats */}
            <div className="p-6 border-b">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-emerald-50 rounded-xl p-5 text-center">
                  <div className="text-4xl font-bold text-emerald-600">{fuelData.total_gallons.toLocaleString()}</div>
                  <div className="text-sm text-slate-600 mt-1">Total Gallons</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-5 text-center">
                  <div className="text-4xl font-bold text-slate-900">{fuelData.total_reports}</div>
                  <div className="text-sm text-slate-600 mt-1">Fuel Entries</div>
                </div>
              </div>
            </div>

            {/* Vehicles Summary */}
            <div className="p-6 border-b">
              <h3 className="font-semibold text-slate-900 mb-4">Fuel by Vehicle</h3>
              {fuelData.vehicles_summary.length === 0 ? (
                <div className="text-center py-8 bg-slate-50 rounded-lg">
                  <Fuel className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">No fuel entries found</p>
                  <p className="text-sm text-slate-500 mt-1">No fuel was recorded during this period.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {fuelData.vehicles_summary.map((vehicle, idx) => (
                    <div 
                      key={vehicle.vehicle_id}
                      className={`rounded-lg p-4 border ${idx === 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-slate-50 border-slate-200'}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${idx === 0 ? 'bg-emerald-600 text-white' : 'bg-slate-300 text-slate-700'}`}>
                            <Fuel className="w-5 h-5" />
                          </div>
                          <div>
                            <div className="font-semibold text-slate-900">Unit {vehicle.unit_number}</div>
                            <div className="text-sm text-slate-500">{vehicle.entries_count} entries</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${idx === 0 ? 'text-emerald-600' : 'text-slate-700'}`}>
                            {vehicle.total_gallons.toLocaleString()}
                          </div>
                          <div className="text-xs text-slate-500">gallons</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* All Entries List */}
            <div className="p-6">
              <h3 className="font-semibold text-slate-900 mb-4">All Fuel Entries</h3>
              {fuelData.all_entries.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No entries found for this period</p>
              ) : (
                <div className="space-y-3 max-h-96 overflow-y-auto">
                  {fuelData.all_entries.map((entry) => (
                    <div key={entry.id} className="flex items-start gap-3 p-3 rounded-lg bg-slate-50 hover:bg-slate-100">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                        <Fuel className="w-5 h-5 text-emerald-600" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-slate-900">Unit {entry.unit_number}</span>
                          <span className="text-emerald-600 font-medium">{entry.gallons} gal</span>
                        </div>
                        <div className="text-sm text-slate-600 mt-1">
                          <span>{entry.odometer?.toLocaleString()} mi</span>
                          <span className="mx-2">•</span>
                          <User className="w-3 h-3 inline mr-1" />
                          {entry.submitted_by}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {new Date(entry.created_at).toLocaleString()}
                        </div>
                        {entry.notes && (
                          <div className="text-xs text-slate-500 mt-1 truncate">{entry.notes}</div>
                        )}
                      </div>
                      {entry.pump_photo && (
                        <img 
                          src={entry.pump_photo} 
                          alt="Pump" 
                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-4 bg-slate-50 border-t border-slate-200 text-center text-xs text-slate-500">
              Fuel Report • Generated {new Date().toLocaleString()} • Pacific Expressway Fleet Management
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button 
              onClick={() => generatePDF("fuel_report")}
              disabled={generating}
              className="w-full sm:flex-1 h-11 sm:h-12 bg-emerald-600 hover:bg-emerald-700 flex items-center justify-center gap-2"
              data-testid="btn-download-fuel-pdf"
            >
              <Download className="w-4 h-4" />
              {generating ? "Generating PDF..." : "Download PDF"}
            </Button>
            <Button 
              variant="outline" 
              onClick={() => { setActiveReport(null); setFuelData(null); }} 
              className="w-full sm:flex-1 h-11 sm:h-12 text-sm sm:text-base"
            >
              Close
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
};
